/**
 * Sistema de Upload de Arquivos - StartKit Rails
 * 
 * Funcionalidades:
 * - Upload único e múltiplo
 * - Validação de tamanho e quantidade
 * - Preview de imagens, PDFs e vídeos
 * - Drag & Drop
 * - Remoção individual de arquivos
 * - Tratamento de erros com preservação de arquivos existentes
 * - Interface responsiva e acessível
 * - Exibição e gerenciamento de arquivos existentes
 * 
 * ===== EXEMPLOS DE USO =====
 * 
 * 1. Input simples para avatar/foto de perfil:
 *    <%= f.input :avatar, as: :file, 
 *        input_html: { accept: 'image/*' }, 
 *        max_size_mb: 5 %>
 * 
 * 2. Input múltiplo para galeria/portfólio:
 *    <%= f.input :portfolio, as: :file, 
 *        file_type: 'multiple', 
 *        input_html: { accept: 'image/*,application/pdf' }, 
 *        max_size_mb: 15, max_files: 8 %>
 * 
 * 3. Input específico para imagens:
 *    <%= f.input :photo, as: :file, 
 *        file_type: 'image', 
 *        input_html: { accept: 'image/jpeg,image/png' }, 
 *        max_size_mb: 10 %>
 * 
 * 4. Input para documentos/anexos:
 *    <%= f.input :attachment, as: :file, 
 *        file_type: 'multiple',
 *        input_html: { accept: 'application/pdf,.doc,.docx,.xls,.xlsx' }, 
 *        max_size_mb: 25, max_files: 3 %>
 * 
 * 5. Input genérico (aceita qualquer tipo):
 *    <%= f.input :file, as: :file, max_size_mb: 50 %>
 * 
 * ===== OPÇÕES DISPONÍVEIS =====
 * 
 * file_type:
 *   - 'file' (padrão): Input simples para um arquivo
 *   - 'image': Otimizado para imagens com preview
 *   - 'multiple': Permite múltiplos arquivos
 * 
 * max_size_mb: Tamanho máximo por arquivo em MB (padrão: 1MB)
 * max_files: Quantidade máxima para 'multiple' (padrão: 5)
 * 
 * input_html.accept: Define tipos aceitos
 *   - 'image/*': Todas as imagens
 *   - 'image/jpeg,image/png': Apenas JPG e PNG
 *   - 'application/pdf': Apenas PDFs
 *   - '.doc,.docx': Por extensão
 * 
 * ===== ÁREA CLICÁVEL =====
 * 
 * Para evitar cliques acidentais, apenas 2 áreas são clicáveis:
 * 1. Card pontilhado de upload (área de drag & drop)
 * 2. Botão "Selecionar arquivo(s)" na parte inferior
 * 
 * O input file tem pointer-events: none para evitar cliques diretos.
 * 
 * ===== VALIDAÇÕES =====
 * 
 * As validações são extraídas automaticamente do hint text:
 * - "15MB" ou "máximo 15MB" → limite de 15MB por arquivo
 * - "Até 5 arquivos" → máximo 5 arquivos para múltiplos
 * 
 * ===== PREVIEW =====
 * 
 * Clique no nome do arquivo ou na miniatura para abrir preview:
 * - Imagens: Visualização em modal responsivo
 * - PDFs: Iframe com o documento
 * - Vídeos: Player com controles
 * - Outros: Ícone com informações do tipo
 * 
 * Fechar preview: ESC ou clicar fora
 * 
 * ===== REMOÇÃO =====
 * 
 * Botão × no canto superior direito de cada arquivo:
 * - Input único: Remove arquivo e reabilita área de upload
 * - Input múltiplo: Remove apenas o arquivo específico
 * - Preserva arquivos existentes em caso de erro na validação
 * 
 * ===== ARQUIVOS EXISTENTES =====
 * 
 * Em formulários de edição, arquivos já anexados são automaticamente exibidos:
 * - Mesma interface visual dos novos uploads
 * - Preview e remoção funcionam normalmente
 * - Remoção marca para exclusão no backend
 * - Compatível com single e multiple uploads
 */

import $ from 'jquery';
import { DirectUpload } from "@rails/activestorage";

// Disponibilizar jQuery globalmente
window.jQuery = $;
window.$ = $;

// === FUNÇÕES PRINCIPAIS ===

function showFileInfo(input) {
  if (input.files && input.files[0]) {
    const file = input.files[0];
    const wrapper = $(input).closest('.file-upload-area');
    const filesList = wrapper.find('.files-list');
    
    // Limpar apenas arquivos novos, manter existentes
    filesList.find('.file-item:not(.existing-file)').remove();
    const fileItem = createFileItemElement(file);
    filesList.append(fileItem);
    
    wrapper.find('.file-list-wrapper').show();
  }
}

// === FUNÇÕES DE VALIDAÇÃO ===

function validateFiles(files, wrapper, isMultiple, isTotal = false) {
  try {
    const hintText = wrapper.find('.hint-text').text() || '';
    const maxSizeMB = extractMaxSize(hintText);
    const maxFiles = extractMaxFiles(hintText);
    const input = wrapper.find('input[type="file"]');
    const acceptedTypes = input.attr('accept') || '';
    
    // Contar arquivos existentes
    const existingFilesCount = wrapper.find('.file-item.existing-file').length;
    
    // Validar quantidade de arquivos (incluindo existentes)
    if (isMultiple && (files.length + existingFilesCount) > maxFiles) {
      return {
        valid: false,
        error: {
          title: 'Muitos arquivos',
          message: `Você pode enviar no máximo ${maxFiles} arquivos. Arquivos selecionados: ${files.length}, existentes: ${existingFilesCount}`
        }
      };
    }
    
    // Validar formato e tamanho de cada arquivo
    for (let file of files) {
      // Validar formato se accept estiver definido
      if (acceptedTypes && acceptedTypes !== '*/*' && !isFileTypeAccepted(file, acceptedTypes)) {
        return {
          valid: false,
          error: {
            title: 'Formato não permitido',
            message: `O arquivo "${file.name}" não é aceito. Formatos permitidos: ${formatAcceptedTypes(acceptedTypes)}`
          }
        };
      }
      
      // Validar tamanho
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSizeMB) {
        return {
          valid: false,
          error: {
            title: 'Arquivo muito grande',
            message: `O arquivo "${file.name}" tem ${formatFileSize(file.size)}. Tamanho máximo: ${maxSizeMB}MB`
          }
        };
      }
    }
    
    return { valid: true };
  } catch (error) {
    console.warn('Erro na validação, permitindo upload:', error);
    return { valid: true };
  }
}

function isFileTypeAccepted(file, acceptedTypes) {
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  const acceptedArray = acceptedTypes.split(',').map(type => type.trim().toLowerCase());
  
  for (let accepted of acceptedArray) {
    // Verificar MIME type exato (ex: image/jpeg)
    if (accepted === fileType) {
      return true;
    }
    
    // Verificar wildcard MIME type (ex: image/*)
    if (accepted.endsWith('/*')) {
      const category = accepted.replace('/*', '');
      if (fileType.startsWith(category + '/')) {
        return true;
      }
    }
    
    // Verificar extensão (ex: .jpg, .png)
    if (accepted.startsWith('.')) {
      const extension = accepted;
      if (fileName.endsWith(extension)) {
        return true;
      }
    }
    
    // Verificar extensões sem ponto (ex: jpg, png)
    if (!accepted.includes('/') && !accepted.startsWith('.')) {
      if (fileName.endsWith('.' + accepted)) {
        return true;
      }
    }
  }
  
  return false;
}

function formatAcceptedTypes(acceptedTypes) {
  return acceptedTypes.split(',').map(type => {
    const cleanType = type.trim();
    if (cleanType.includes('/')) {
      // MIME type (ex: image/jpeg -> JPG)
      return cleanType.split('/')[1].toUpperCase();
    } else if (cleanType.startsWith('.')) {
      // Extensão com ponto (ex: .jpg -> JPG)
      return cleanType.substring(1).toUpperCase();
    } else {
      // Extensão sem ponto (ex: jpg -> JPG)
      return cleanType.toUpperCase();
    }
  }).join(', ');
}

function extractMaxSize(hintText) {
  try {
    const match = hintText.match(/(\d+)MB/i);
    return match ? parseInt(match[1]) : 10;
  } catch (error) {
    return 10;
  }
}

function extractMaxFiles(hintText) {
  try {
    const match = hintText.match(/(?:até|máximo)\s*(\d+)\s*arquivos?/i);
    return match ? parseInt(match[1]) : 5;
  } catch (error) {
    return 5;
  }
}

// === FUNÇÕES UTILITÁRIAS ===

function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getExistingFiles(wrapper) {
  const input = wrapper.find('input[type="file"]')[0];
  return input && input.files ? Array.from(input.files) : [];
}

function updateInputFiles(inputElement, newFiles) {
  const dataTransfer = new DataTransfer();
  newFiles.forEach(file => dataTransfer.items.add(file));
  inputElement.files = dataTransfer.files;
}

function getFileIcon(fileType) {
  const icons = {
    'image': 'far fa-image',
    'video': 'far fa-file-video',
    'pdf': 'far fa-file-pdf',
    'document': 'far fa-file-alt',
    'default': 'far fa-file'
  };
  
  if (fileType.startsWith('image/')) return icons.image;
  if (fileType.startsWith('video/')) return icons.video;
  if (fileType === 'application/pdf') return icons.pdf;
  if (fileType.includes('document')) return icons.document;
  
  return icons.default;
}

function createFilePreview(file) {
  const preview = $('<div>').addClass('file-preview');
  
  if (file.type.startsWith('image/')) {
    const objectUrl = URL.createObjectURL(file);
    const img = $('<img>').attr('src', objectUrl);
    
    // Revogar URL quando a imagem carregar
    img.on('load', () => {
      URL.revokeObjectURL(objectUrl);
    });
    
    // Revogar URL se houver erro
    img.on('error', () => {
      URL.revokeObjectURL(objectUrl);
      img.attr('src', ''); // Limpar src para evitar tentativas de carregamento
      img.attr('alt', 'Erro ao carregar imagem');
    });
    
    preview.append(img);
  } else {
    const icon = $('<div>').addClass('file-icon')
      .append($('<i>').addClass(getFileIcon(file.type)));
    preview.append(icon);
  }
  
  return preview;
}

function createFileItemElement(file) {
  const fileItem = $('<div>').addClass('file-item');
  
  // Preview clicável
  const preview = createFilePreview(file);
  preview.addClass('preview-clickable').on('click.file-input-events', function(e) {
    e.stopPropagation();
    e.preventDefault();
    showFilePreviewModal(file);
  });
  
  // Nome do arquivo clicável
  const fileName = $('<span>').addClass('file-name').text(file.name);
  fileName.on('click.file-input-events', function(e) {
    e.stopPropagation();
    e.preventDefault();
    showFilePreviewModal(file);
  });
  
  // Informações do arquivo
  const fileInfo = $('<div>').addClass('file-info')
    .append(fileName)
    .append(
      $('<div>').addClass('file-meta')
        .append(
          $('<span>').addClass('file-size')
            .append($('<i>').addClass('far fa-hard-drive'))
            .append(' ' + formatFileSize(file.size))
        )
        .append(
          $('<span>').addClass('file-type')
            .append($('<i>').addClass(getFileIcon(file.type)))
            .append(' ' + file.type.split('/')[1].toUpperCase())
        )
    );
  
  // Botão de remoção
  const removeButton = $('<button>').attr({
    type: 'button',
    class: 'btn-remove',
    'aria-label': 'Remover arquivo',
    title: 'Remover arquivo'
  });
  
  removeButton.on('click.file-input-events', function(e) {
    e.stopPropagation();
    e.preventDefault();
    const inputElement = $(this).closest('.file-upload-area').find('input[type="file"]')[0];
    fileItem.remove();
    updateFileInputAfterRemoval(inputElement, file);
  });
  
  fileItem.append(preview).append(fileInfo).append(removeButton);
  return fileItem;
}

function updateFileInputAfterRemoval(inputElement, removedFile) {
  try {
    const wrapper = $(inputElement).closest('.file-upload-area');
    const remainingItems = wrapper.find('.files-list .file-item').length;
    
    if (remainingItems === 0) {
      // Último arquivo removido - limpar tudo
      inputElement.value = '';
      inputElement.files = new DataTransfer().files;
      wrapper.find('.file-list-wrapper').hide();
      wrapper.find('.upload-progress').hide();
      
      const uploadCard = wrapper.find('.upload-card');
      uploadCard.css('pointer-events', 'auto').removeClass('has-files');
    } else {
      // Ainda há arquivos - manter estado baseado no tipo
      const uploadCard = wrapper.find('.upload-card');
      const isMultiple = $(inputElement).attr('multiple');
      
      if (isMultiple) {
        uploadCard.css('pointer-events', 'auto').addClass('has-files');
      } else {
        uploadCard.css('pointer-events', 'none').addClass('has-files');
      }
    }
  } catch (error) {
    console.warn('Erro ao remover arquivo:', error);
  }
}

// === FUNÇÕES PARA ARQUIVOS EXISTENTES ===

function initializeExistingFiles(wrapper) {
  try {
    // Limpar event listeners anteriores para arquivos existentes
    wrapper.find('.existing-file').off('.file-input-events');
    
    // Configurar eventos para arquivos existentes
    wrapper.find('.existing-file [data-existing-file="true"]').each(function() {
      const element = $(this);
      const attachmentId = element.data('attachment-id');
      
      if (element.hasClass('preview-clickable') || element.hasClass('file-name')) {
        element.on('click.file-input-events', function(e) {
          e.stopPropagation();
          e.preventDefault();
          showExistingFilePreview(attachmentId, wrapper);
        });
      }
    });
    
    // Configurar remoção de arquivos existentes
    wrapper.find('.existing-file .btn-remove[data-existing-file="true"]').on('click.file-input-events', function(e) {
      e.stopPropagation();
      e.preventDefault();
      removeExistingFile($(this), wrapper);
    });
    
    // Ajustar estado inicial baseado em arquivos existentes
    const hasExistingFiles = wrapper.find('.existing-file').length > 0;
    if (hasExistingFiles) {
      const isMultiple = wrapper.find('input[type="file"]').attr('multiple');
      const uploadCard = wrapper.find('.upload-card');
      
      if (isMultiple) {
        uploadCard.css('pointer-events', 'auto').addClass('has-files');
      } else {
        uploadCard.css('pointer-events', 'auto').addClass('has-files');
      }
    }
  } catch (error) {
    console.warn('Erro ao inicializar arquivos existentes:', error);
  }
}

function showExistingFilePreview(attachmentId, wrapper) {
  try {
    const fileItem = wrapper.find(`[data-attachment-id="${attachmentId}"]`).closest('.file-item');
    const fileName = fileItem.find('.file-name').text();
    const fileSize = fileItem.find('.file-size').text().replace(/.*\s/, ''); // Pegar apenas o tamanho
    const isImage = fileItem.find('.file-preview img').length > 0;
    
    if (isImage) {
      const imgSrc = fileItem.find('.file-preview img').attr('src');
      showExistingImageModal(fileName, imgSrc, fileSize);
    } else {
      // Para outros tipos, abrir em nova aba ou mostrar info
      const attachment = fileItem.find('.file-preview');
      showExistingFileModal(fileName, fileSize, attachment);
    }
  } catch (error) {
    console.warn('Erro ao mostrar preview de arquivo existente:', error);
  }
}

function showExistingImageModal(fileName, imageSrc, fileSize) {
  const modal = $('<div>').addClass('file-preview-modal').html(`
    <div class="modal-backdrop"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h5>${fileName}</h5>
        <button type="button" class="btn-close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <img src="${imageSrc}" style="max-width: 100%; max-height: 70vh; object-fit: contain;">
      </div>
      <div class="modal-footer">
        <span class="file-info">${fileSize} • Arquivo existente</span>
      </div>
    </div>
  `);
  
  // Eventos para fechar o modal
  modal.find('.modal-backdrop, .btn-close').on('click', function() {
    modal.remove();
  });
  
  // Fechar com ESC
  $(document).on('keydown.existing-modal', function(e) {
    if (e.key === 'Escape') {
      modal.remove();
      $(document).off('keydown.existing-modal');
    }
  });
  
  $('body').append(modal);
}

function showExistingFileModal(fileName, fileSize, attachment) {
  const modal = $('<div>').addClass('file-preview-modal').html(`
    <div class="modal-backdrop"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h5>${fileName}</h5>
        <button type="button" class="btn-close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="text-center p-4">
          <i class="far fa-file" style="font-size: 4rem; color: var(--bs-gray-500);"></i>
          <p class="mt-3">Arquivo existente</p>
          <p class="text-muted">${fileName}</p>
        </div>
      </div>
      <div class="modal-footer">
        <span class="file-info">${fileSize} • Arquivo existente</span>
      </div>
    </div>
  `);
  
  // Eventos para fechar o modal
  modal.find('.modal-backdrop, .btn-close').on('click', function() {
    modal.remove();
  });
  
  // Fechar com ESC
  $(document).on('keydown.existing-modal', function(e) {
    if (e.key === 'Escape') {
      modal.remove();
      $(document).off('keydown.existing-modal');
    }
  });
  
  $('body').append(modal);
}

function removeExistingFile(removeButton, wrapper) {
  try {
    const attachmentId = removeButton.data('attachment-id');
    const fileItem = removeButton.closest('.file-item');
    
    // Obter o nome do campo do input
    const inputElement = wrapper.find('input[type="file"]')[0];
    const inputName = inputElement.name;
    
    // Extrair o nome base do campo (ex: "user[avatar]" -> "avatar")
    const fieldMatch = inputName.match(/\[([^\]]+)\]$/);
    const fieldName = fieldMatch ? fieldMatch[1] : 'attachment';
    
    // Criar campo hidden para marcar remoção usando o padrão Rails
    // Formato: field_name_remove = "1"
    const removeFieldName = inputName.replace(/\[([^\]]+)\]$/, `[${fieldName}_remove]`);
    
    // Verificar se já existe um campo de remoção e removê-lo
    wrapper.find(`input[name="${removeFieldName}"]`).remove();
    
    // Criar novo campo hidden para marcar remoção
    const hiddenField = $('<input>').attr({
      type: 'hidden',
      name: removeFieldName,
      value: '1'
    });
    
    wrapper.append(hiddenField);
    
    // Remover visualmente o item
    fileItem.fadeOut(300, function() {
      $(this).remove();
      
      // Verificar se ainda há arquivos
      const remainingFiles = wrapper.find('.file-item').length;
      if (remainingFiles === 0) {
        wrapper.find('.file-list-wrapper').hide();
        wrapper.find('.upload-card').css('pointer-events', 'auto').removeClass('has-files');
        
        // Atualizar texto do botão para estado inicial
        updateButtonText(wrapper, false);
      }
    });
    
  } catch (error) {
    console.warn('Erro ao remover arquivo existente:', error);
  }
}

function updateButtonText(wrapper, hasFiles) {
  try {
    const button = wrapper.find('button[data-input-id]');
    const isMultiple = wrapper.find('input[type="file"]').attr('multiple');
    const fileType = wrapper.data('input-id') ? wrapper.attr('class').match(/(image|multiple|file)-upload/)?.[1] || 'file' : 'file';
    
    let buttonText;
    if (hasFiles) {
      if (isMultiple) {
        buttonText = "Adicionar mais arquivos";
      } else {
        switch (fileType) {
          case 'image':
            buttonText = "Alterar imagem";
            break;
          default:
            buttonText = "Alterar arquivo";
        }
      }
    } else {
      buttonText = `Selecionar arquivo${isMultiple ? 's' : ''}`;
    }
    
    // Atualizar apenas o texto, mantendo o ícone
    const buttonContent = button.html();
    const iconMatch = buttonContent.match(/(<svg[^>]*>.*?<\/svg>)/);
    const icon = iconMatch ? iconMatch[1] : '';
    
    button.html(`${icon} ${buttonText}`);
  } catch (error) {
    console.warn('Erro ao atualizar texto do botão:', error);
  }
}

// === FUNÇÕES DE FEEDBACK ===

function showReplacementWarning(wrapper, newFile, onConfirm, onCancel) {
  try {
    // Remover avisos anteriores
    wrapper.find('.file-replacement-warning').remove();
    
    const warningCard = $(`
      <div class="file-replacement-warning">
        <div class="warning-content">
          <i class="fas fa-exclamation-triangle text-warning"></i>
          <div class="warning-details">
            <span class="warning-title">Substituir arquivo?</span>
            <span class="warning-message">Já existe um arquivo anexado. O novo arquivo "${newFile.name}" substituirá o arquivo atual.</span>
          </div>
        </div>
        <div class="warning-actions">
          <button type="button" class="btn btn-sm btn-warning btn-confirm-replace">
            <i class="fas fa-check"></i> Substituir
          </button>
          <button type="button" class="btn btn-sm btn-secondary btn-cancel-replace">
            <i class="fas fa-times"></i> Cancelar
          </button>
        </div>
      </div>
    `);
    
    // Event listeners para os botões
    warningCard.find('.btn-confirm-replace').on('click', function() {
      warningCard.remove();
      onConfirm();
    });
    
    warningCard.find('.btn-cancel-replace').on('click', function() {
      warningCard.remove();
      onCancel();
    });
    
    // Inserir após o upload card
    wrapper.find('.upload-card').after(warningCard);
    
    // Auto-remover após 15 segundos se não houver ação
    setTimeout(() => {
      if (warningCard.parent().length) {
        warningCard.fadeOut(() => {
          warningCard.remove();
          onCancel();
        });
      }
    }, 15000);
  } catch (error) {
    console.warn('Erro ao mostrar aviso de substituição:', error);
    onConfirm(); // Em caso de erro, prosseguir com a substituição
  }
}

function proceedWithReplacement(inputElement, wrapper, newFiles) {
  try {
    // Remover arquivos existentes visualmente
    wrapper.find('.existing-file').fadeOut(300, function() {
      $(this).remove();
    });
    
    // Remover arquivos novos anteriores
    wrapper.find('.files-list .file-item:not(.existing-file)').fadeOut(300, function() {
      $(this).remove();
    });
    
    // Marcar arquivo existente para remoção se houver
    const existingFile = wrapper.find('.existing-file');
    if (existingFile.length > 0) {
      const inputName = inputElement.name;
      const fieldMatch = inputName.match(/\[([^\]]+)\]$/);
      const fieldName = fieldMatch ? fieldMatch[1] : 'attachment';
      const removeFieldName = inputName.replace(/\[([^\]]+)\]$/, `[${fieldName}_remove]`);
      
      // Remover campo de remoção anterior se existir
      wrapper.find(`input[name="${removeFieldName}"]`).remove();
      
      // Adicionar campo hidden para marcar remoção
      const hiddenField = $('<input>').attr({
        type: 'hidden',
        name: removeFieldName,
        value: '1'
      });
      wrapper.append(hiddenField);
    }
    
    // Processar o novo arquivo
    setTimeout(() => {
      showFileInfo(inputElement);
      wrapper.find('.file-list-wrapper').show();
      
      // Atualizar estado do upload card
      const uploadCard = wrapper.find('.upload-card');
      uploadCard.css('pointer-events', 'auto').addClass('has-files');
      
      // Atualizar texto do botão
      updateButtonText(wrapper, true);
    }, 350); // Delay para permitir a animação de remoção
    
  } catch (error) {
    console.warn('Erro ao processar substituição:', error);
    // Em caso de erro, processar normalmente
    showFileInfo(inputElement);
  }
}

function showError(wrapper, error) {
  try {
    // Remover erro anterior
    wrapper.find('.file-error-card').remove();
    
    const errorCard = $(`
      <div class="file-error-card">
        <div class="error-content">
          <i class="fas fa-exclamation-triangle"></i>
          <div class="error-details">
            <span class="error-title">${error.title}</span>
            <span class="error-message">${error.message}</span>
          </div>
        </div>
        <button type="button" class="btn-close-error">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `);
    
    // Evento para fechar erro
    errorCard.find('.btn-close-error').on('click', function() {
      errorCard.remove();
    });
    
    // Inserir após o upload card
    wrapper.find('.upload-card').after(errorCard);
    
    // Auto-remover após 8 segundos
    setTimeout(() => {
      errorCard.fadeOut(() => errorCard.remove());
    }, 8000);
  } catch (error) {
    console.warn('Erro ao mostrar mensagem de erro:', error);
  }
}

function updateUploadProgress(wrapper, loaded, total) {
  try {
    const progressWrapper = wrapper.find('.upload-progress');
    const percentage = Math.round((loaded / total) * 100);
    
    progressWrapper.find('.progress-bar').css('width', percentage + '%');
    progressWrapper.find('.progress-percentage').text(percentage + '%');
    progressWrapper.find('.progress-size').text(
      formatFileSize(loaded) + ' de ' + formatFileSize(total)
    );
    
    if (percentage === 100) {
      setTimeout(() => progressWrapper.hide(), 1500);
    }
  } catch (error) {
    console.warn('Erro ao atualizar progresso:', error);
  }
}

// === MODAL DE PREVIEW ===

function showFilePreviewModal(file) {
  try {
    const objectUrl = file.type.startsWith('image/') || file.type === 'application/pdf' || file.type.startsWith('video/') 
      ? URL.createObjectURL(file) 
      : null;

    const modal = $('<div>').addClass('file-preview-modal').html(`
      <div class="modal-backdrop"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h5>${file.name}</h5>
          <button type="button" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          ${createPreviewContent(file, objectUrl)}
        </div>
        <div class="modal-footer">
          <span class="file-info">${formatFileSize(file.size)} • ${file.type}</span>
        </div>
      </div>
    `);
    
    // Eventos para fechar o modal
    modal.find('.modal-backdrop, .btn-close').on('click', function() {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      modal.remove();
    });
    
    // Fechar com ESC
    $(document).on('keydown.modal', function(e) {
      if (e.key === 'Escape') {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        modal.remove();
        $(document).off('keydown.modal');
      }
    });
    
    // Remover o evento ESC quando o modal for removido
    modal.on('remove', function() {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      $(document).off('keydown.modal');
    });
    
    $('body').append(modal);
  } catch (error) {
    console.warn('Erro ao mostrar preview:', error);
  }
}

function createPreviewContent(file, objectUrl) {
  try {
    if (file.type.startsWith('image/')) {
      return `<img src="${objectUrl}" style="max-width: 100%; max-height: 70vh; object-fit: contain;">`;
    } else if (file.type === 'application/pdf') {
      return `<iframe src="${objectUrl}" style="width: 100%; height: 70vh; border: none;"></iframe>`;
    } else if (file.type.startsWith('video/')) {
      return `<video controls style="max-width: 100%; max-height: 70vh;">
                <source src="${objectUrl}" type="${file.type}">
                Seu navegador não suporta reprodução de vídeo.
              </video>`;
    } else {
      return `<div class="text-center p-4">
                <i class="${getFileIcon(file.type)}" style="font-size: 4rem; color: var(--bs-gray-500);"></i>
                <p class="mt-3">Preview não disponível para este tipo de arquivo.</p>
                <p class="text-muted">Tipo: ${file.type}</p>
              </div>`;
    }
  } catch (error) {
    return `<div class="text-center p-4">
              <p>Erro ao carregar preview do arquivo.</p>
            </div>`;
  }
}

// === INICIALIZAÇÃO ===

function initializeFileInputs() {
  // Inicializar todos os inputs de arquivo
  $('input[type="file"]').each(function() {
    const input = $(this);
    const wrapper = input.closest('.file-upload-area');
    
    if (wrapper.length === 0) {
      console.warn('File input sem wrapper encontrado:', this);
      return;
    }
    
    // Verificar se já foi inicializado para evitar duplicação
    if (input.data('file-input-initialized')) {
      return;
    }
    input.data('file-input-initialized', true);

    // Limpar event listeners anteriores para evitar duplicação
    wrapper.off('.file-input-events');
    
    // Inicializar arquivos existentes primeiro
    initializeExistingFiles(wrapper);
    
    // Remover eventos de clique do input para evitar cliques acidentais
    input.css('pointer-events', 'none');
    
    // Adicionar classe para drag & drop apenas no card de upload
    const uploadCard = wrapper.find('.upload-card');
    uploadCard.on('dragover.file-input-events', function(e) {
      e.preventDefault();
      wrapper.addClass('dragover');
    }).on('dragleave.file-input-events drop.file-input-events', function(e) {
      e.preventDefault();
      wrapper.removeClass('dragover');
      
      if (e.type === 'drop') {
        const droppedFiles = Array.from(e.originalEvent.dataTransfer.files);
        if (droppedFiles.length > 0) {
          const isMultiple = input.attr('multiple');
          
          // Validação específica para campos single com arquivos existentes (drag & drop)
          if (!isMultiple) {
            const hasExistingFile = wrapper.find('.existing-file').length > 0;
            const hasCurrentNewFile = wrapper.find('.files-list .file-item:not(.existing-file)').length > 0;
            
            if (hasExistingFile || hasCurrentNewFile) {
              // Mostrar aviso sobre substituição
              showReplacementWarning(wrapper, droppedFiles[0], () => {
                // Callback para confirmar substituição
                updateInputFiles(input[0], droppedFiles);
                proceedWithReplacement(input[0], wrapper, droppedFiles);
              }, () => {
                // Callback para cancelar - não fazer nada
              });
              return;
            }
          }
          
          // Validar arquivos antes de processar
          const validationResult = validateFiles(droppedFiles, wrapper, isMultiple);
          if (!validationResult.valid) {
            showError(wrapper, validationResult.error);
            return;
          }
          
          if (isMultiple) {
            // Para múltiplos, somar com existentes
            const existingFiles = getExistingFiles(wrapper);
            const allFiles = [...existingFiles, ...droppedFiles];
            updateInputFiles(input[0], allFiles);
          } else {
            // Para single, substituir
            updateInputFiles(input[0], droppedFiles);
          }
          
          input.trigger('change');
        }
      }
    });

    // Event listener para cliques no upload card (substitui o onclick inline)
    uploadCard.on('click.file-input-events', function(e) {
      // Verificar se o clique não foi em um botão de remoção
      if (!$(e.target).closest('.btn-remove').length) {
        const inputId = $(this).data('input-id');
        if (inputId) {
          document.getElementById(inputId).click();
        }
      }
    });

    // Event listener para o botão de seleção
    wrapper.find('button[data-input-id]').on('click.file-input-events', function(e) {
      e.preventDefault();
      e.stopPropagation();
      const inputId = $(this).data('input-id');
      if (inputId) {
        document.getElementById(inputId).click();
      }
    });

    // Suporte a teclado para o upload card
    uploadCard.on('keydown.file-input-events', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!$(e.target).closest('.btn-remove').length) {
          const inputId = $(this).data('input-id');
          if (inputId) {
            document.getElementById(inputId).click();
          }
        }
      }
    });
    
    // Evento de mudança do input
    input.on('change.file-input-events', function() {
      // Proteção contra chamadas duplicadas
      const timestamp = Date.now();
      const lastChangeTime = $(this).data('lastChangeTime') || 0;
      
      if (timestamp - lastChangeTime < 100) return;
      
      $(this).data('lastChangeTime', timestamp);
      
      try {
        const newFiles = Array.from(this.files);
        const isMultiple = $(this).attr('multiple');
        
        if (newFiles.length === 0) {
          return;
        }
        
        // Validação específica para campos single com arquivos existentes
        if (!isMultiple) {
          const hasExistingFile = wrapper.find('.existing-file').length > 0;
          const hasCurrentNewFile = wrapper.find('.files-list .file-item:not(.existing-file)').length > 0;
          
          if (hasExistingFile || hasCurrentNewFile) {
            // Mostrar aviso sobre substituição
            showReplacementWarning(wrapper, newFiles[0], () => {
              // Callback para confirmar substituição
              proceedWithReplacement(this, wrapper, newFiles);
            }, () => {
              // Callback para cancelar
              this.value = '';
              this.files = new DataTransfer().files;
            });
            return;
          }
        }
        
        // Validar arquivos antes de processar
        const validationResult = validateFiles(newFiles, wrapper, isMultiple);
        if (!validationResult.valid) {
          // Limpar input mas verificar se há arquivos existentes
          this.value = '';
          this.files = new DataTransfer().files;
          
          const currentFilesList = wrapper.find('.files-list .file-item:not(.existing-file)');
          if (currentFilesList.length === 0) {
            // Se não há arquivos novos, verificar se há existentes
            const hasExistingFiles = wrapper.find('.files-list .existing-file').length > 0;
            if (!hasExistingFiles) {
              wrapper.find('.file-list-wrapper').hide();
              wrapper.find('.upload-progress').hide();
              const uploadCard = wrapper.find('.upload-card');
              uploadCard.css('pointer-events', 'auto').removeClass('has-files');
            }
          }
          
          showError(wrapper, validationResult.error);
          return;
        }
        
        // Processar arquivos múltiplos
        if (isMultiple) {
          const filesList = wrapper.find('.files-list');
          const currentNewFiles = filesList.find('.file-item:not(.existing-file)');
          const existingFilesCount = filesList.find('.existing-file').length;
          const isFirstSelection = currentNewFiles.length === 0;
          
          if (!isFirstSelection) {
            // Validar limite total
            const totalCount = currentNewFiles.length + existingFilesCount + newFiles.length;
            const maxFiles = extractMaxFiles(wrapper.find('.hint-text').text() || '');
            
            if (totalCount > maxFiles) {
              // Limpar apenas o input, manter a lista visual existente
              this.value = '';
              this.files = new DataTransfer().files;
              showError(wrapper, {
                title: 'Muitos arquivos',
                message: `Máximo ${maxFiles} arquivos permitidos. Você já tem ${currentNewFiles.length + existingFilesCount} arquivo${(currentNewFiles.length + existingFilesCount) > 1 ? 's' : ''}.`
              });
              return;
            }
          }
          
          // Adicionar arquivos à lista
          newFiles.forEach(file => {
            const fileItem = createFileItemElement(file);
            filesList.append(fileItem);
          });
          
          if (isFirstSelection && existingFilesCount === 0) {
            wrapper.find('.file-list-wrapper').show();
          }
          
          // Para múltiplos arquivos, manter o card clicável para adicionar mais
          const uploadCard = wrapper.find('.upload-card');
          uploadCard.css('pointer-events', 'auto').addClass('has-files');
        } else {
          showFileInfo(this);
          
          // Para arquivo único, desabilitar card se não for múltiplo
          const uploadCard = wrapper.find('.upload-card');
          uploadCard.css('pointer-events', 'auto').addClass('has-files');
        }
        
        // Só adicionar barra de progresso se chegou até aqui (sem erros)
        let progressWrapper = wrapper.find('.upload-progress');
        if (!progressWrapper.length) {
          progressWrapper = $('<div>').addClass('upload-progress').html(`
            <div class="progress-info">
              <span class="progress-percentage">0%</span>
              <span class="progress-size">0 KB de 0 KB</span>
            </div>
            <div class="progress">
              <div class="progress-bar" style="width: 0%"></div>
            </div>
          `);
          
          // Inserir após o upload-card para garantir a ordem correta
          const uploadCard = wrapper.find('.upload-card');
          if (uploadCard.length) {
            uploadCard.after(progressWrapper);
          } else {
            wrapper.prepend(progressWrapper);
          }
        }
        
        // Mostrar a barra de progresso apenas se há arquivos válidos
        if (this.files.length > 0) {
          progressWrapper.show();
          
          // Simular progresso (substitua por seu código real de upload)
          const files = Array.from(this.files);
          const totalSize = files.reduce((acc, file) => acc + file.size, 0);
          let loaded = 0;
          
          const interval = setInterval(() => {
            loaded = Math.min(loaded + totalSize * 0.1, totalSize);
            updateUploadProgress(wrapper, loaded, totalSize);
            
            if (loaded >= totalSize) {
              clearInterval(interval);
            }
          }, 200);
        }
        
      } catch (error) {
        console.error('Erro no evento change:', error);
      }
    });
  });
}

// Inicializar quando o DOM estiver pronto
$(document).ready(initializeFileInputs);

// Reinicializar em navegação Turbo
document.addEventListener('turbo:load', initializeFileInputs);
document.addEventListener('turbo:render', initializeFileInputs);

// Exportar funções para uso externo
window.showFileInfo = showFileInfo;